import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';
import { env } from 'config/env';
import { FFDefaults } from './featureFlags.generated';
import type { FeatureFlag } from './featureFlags.generated';
import type { IBrowserClient } from '@splitsoftware/splitio/types/splitio';

export interface ResolvedFlag {
  treatment: string;
  // Raw dynamic config JSON string attached to the treatment, or null when the
  // caller did not request config or the treatment fell back to the default.
  config: Nullable<string>;
}

export function flagLoading(ready: boolean, timeout: boolean): boolean {
  return ready || env.STORYBOOK === 'true' ? false : !timeout;
}

/**
 * Pure resolution shared by the flag hooks: evaluates the treatment (collapsing
 * 'control' to the FFDefaults fallback), records the evaluation for RUM
 * correlation, and optionally reads the treatment's dynamic config.
 *
 * The hooks call useSplitClient themselves and pass the tuple in, so this stays a
 * plain function (no hook, no useSplitClient import).
 *
 * Provider note: the two Split SDK calls below (`getTreatment` /
 * `getTreatmentWithConfig`) are the only provider-specific lines in the flag-hook
 * stack. An OpenFeature move reimplements just this function.
 */
export function resolveFlag(
  client: Nullable<IBrowserClient>,
  ready: boolean,
  timeout: boolean,
  flagName: FeatureFlag,
  withConfig: boolean
): ResolvedFlag {
  const fallbackTreatment: string = FFDefaults[flagName];
  const fallback: ResolvedFlag = { treatment: fallbackTreatment, config: null };

  if (client == null && env.STORYBOOK === 'true') return fallback;

  if (timeout || (ready && client == null)) {
    analytics.addContext(globalContext.workspace.featureFlag(flagName), fallbackTreatment);
    return fallback;
  }

  if (ready && client != null) {
    const { treatment, config } = withConfig
      ? client.getTreatmentWithConfig(flagName)
      : { treatment: client.getTreatment(flagName), config: null };
    const value = treatment === 'control' ? fallbackTreatment : treatment;
    analytics.addContext(globalContext.workspace.featureFlag(flagName), value);
    return { treatment: value, config: treatment === 'control' ? null : config };
  }

  return fallback;
}
